import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LineChart, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 25; // tickers per request — keeps us under the 60s ingress cap

/**
 * AdminWarmYieldHistoryButton — Round 93ac (Feb 2026)
 *
 * Pre-warms `yield_history_cache` for every distinct ticker held in
 * any user's portfolio OR watchlist. Drives the Stock Modal "Yield
 * History" tab (3-year price + yield chart + annual dividend bars).
 *
 * Without this, the tab has to lazy-fetch on first user click after
 * a redeploy. Massive can occasionally need a moment to spin up the
 * aggs endpoint for cold tickers, and the Yahoo fallback can be IP-
 * rate-limited right after the pod's fresh IP starts making outbound
 * requests. Pre-warming eliminates both failure modes.
 *
 * Chunked (25 tickers/request) to avoid 504 Gateway Timeouts on the
 * production ingress. Default skips tickers cached <24h with Massive
 * provenance; Yahoo-era rows always re-attempt so the cache slowly
 * migrates to Massive over multiple runs.
 *
 * A complementary nightly cron (`_run_warm_yield_history_cache_job`
 * at 4:30 AM ET) handles the long-tail maintenance; a per-add
 * lazy warmer hooks new buys/watches instantly.
 */
export default function AdminWarmYieldHistoryButton() {
  // Round P111u — self-gate; hides even if wrapper is bypassed.
  const { user: _authUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [force, setForce] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const start = async () => {
    setRunning(true);
    setError(null);
    setProgress({ warmed: 0, skipped_fresh: 0, errors: 0, total: 0, offset: 0 });
    try {
      let offset = 0;
      let total = 0;
      let warmed = 0;
      let skipped = 0;
      let errors = 0;
      while (true) {
        const r = await axios.post(
          `${API}/admin/warm-yield-history-cache?limit=${CHUNK}&offset=${offset}&force=${force ? 'true' : 'false'}`,
          null,
          { withCredentials: true, timeout: 90000 },
        );
        const d = r.data || {};
        warmed += d.warmed || 0;
        skipped += d.skipped_fresh || 0;
        errors += d.errors || 0;
        total = d.total || total;
        offset = d.next_offset || offset + CHUNK;
        setProgress({ warmed, skipped_fresh: skipped, errors, total, offset: Math.min(offset, total) });
        if (!d.has_more) break;
      }
      toast.success(
        `Warmed ${warmed} · Skipped ${skipped} fresh · ${errors} errors · ${total} total tickers`,
      );
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Cache warm failed');
    } finally {
      setRunning(false);
    }
  };

  const close = () => {
    if (running) return;
    setOpen(false);
    setProgress(null);
    setError(null);
    setForce(false);
  };

  // Round P111u — self-gate (post-hooks; safe with Rules of Hooks).
  if (_authUser?.role !== 'admin') return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-warm-yh-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-violet-500/15 hover:bg-violet-500/25 text-violet-900 dark:text-violet-200 border border-violet-500/40 transition-colors"
      >
        <LineChart className="w-4 h-4" />
        Warm Yield History cache
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={close}
          data-testid="admin-warm-yh-modal"
        >
          <div
            className="relative w-full max-w-lg bg-card border border-violet-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Warm Yield History cache</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Pulls Massive 3-yr price + dividend history for every held + watchlist ticker.
                </p>
              </div>
              <button onClick={close} aria-label="Close" disabled={running} className="p-1 rounded hover:bg-muted/40 disabled:opacity-30">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {!running && !progress && (
                <>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={force}
                      onChange={(e) => setForce(e.target.checked)}
                      className="mt-0.5 w-3.5 h-3.5 rounded border-border accent-violet-500"
                      data-testid="admin-warm-yh-force"
                    />
                    <span>
                      <span className="font-bold">Force re-fetch</span> — ignore the 24h-Massive freshness check and re-pull every ticker (slower; only needed if you suspect stale data or want to wipe out Yahoo-era rows in one pass).
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={start}
                    data-testid="admin-warm-yh-start"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-violet-500/20 hover:bg-violet-500/30 text-violet-100 border border-violet-500/40 transition-colors"
                  >
                    <LineChart className="w-4 h-4" />
                    Start cache warm
                  </button>
                </>
              )}
              {running && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="admin-warm-yh-running">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Warming chunk {progress?.offset || 0} of {progress?.total || '?'}…
                </div>
              )}
              {progress && (
                <div className="rounded bg-muted/40 px-3 py-2 text-xs font-mono space-y-0.5" data-testid="admin-warm-yh-progress">
                  <div>Total held + watched tickers: <span className="font-bold">{progress.total}</span></div>
                  <div>Newly warmed: <span className="font-bold text-violet-300">{progress.warmed}</span></div>
                  <div>Skipped (Massive &lt; 24h): <span className="font-bold text-muted-foreground">{progress.skipped_fresh}</span></div>
                  {progress.errors > 0 && (
                    <div>Errors: <span className="font-bold text-rose-300">{progress.errors}</span></div>
                  )}
                </div>
              )}
              {error && (
                <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200" data-testid="admin-warm-yh-error">{error}</div>
              )}
              {!running && progress && !error && progress.offset >= progress.total && (
                <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/40 text-sm text-emerald-200" data-testid="admin-warm-yh-done">
                  ✅ Done. The Stock Modal Yield History tab will now load instantly for every held + watchlist ticker.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
